import FolderRounded from '@mui/icons-material/FolderRounded';
import { styled } from '@mui/material/styles';
import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view';
import { TreeItemIconContainer } from '@mui/x-tree-view/TreeItem';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { useTreeItemModel } from '@mui/x-tree-view/hooks';
import {
  useTreeItem,
  UseTreeItemParameters,
} from '@mui/x-tree-view/useTreeItem';
import * as React from 'react';
import { FileType, getIconFromFileType } from '../../helpers/fileIcons';
import { CustomCheckbox } from './CustomCheckbox';
import { CustomLabel } from './CustomLabel';
import { getSignatureState, SignatureChip, SignatureFacts } from './SignatureChip';
import './CustomTreeItem.scss';

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

const TreeItemRoot = styled('li', {
  shouldForwardProp: (prop) =>
    prop !== 'isRoot' && prop !== 'isEven' && prop !== 'isFile',
})<{
  isRoot: boolean;
  isEven: boolean;
}>(({ isRoot, isEven }) => ({
  backgroundColor: isRoot
    ? 'var(--joy-palette-background-level1)'
    : isEven
    ? 'var(--joy-palette-background-body)'
    : 'var(--joy-palette-background-level1)',
  transition: 'background-color 0.2s ease-in-out',
}));

const TreeItemContent = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
  paddingLeft: `calc(${theme.spacing(
    1
  )} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  flexDirection: 'row',
  borderRadius: theme.spacing(0.7),
  fontWeight: 500,
  '&[data-expanded]:not([data-focused], [data-selected]) .labelIcon': {
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: '16px',
      top: '44px',
      height: 'calc(100% - 48px)',
      width: '1.5px',
      backgroundColor: theme.palette.grey[500],
      ...theme.applyStyles('light', {
        backgroundColor: theme.palette.grey[300],
      }),
    },
  },
  [`&[data-focused], &[data-selected]`]: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    ...theme.applyStyles('light', {
      backgroundColor: theme.palette.primary.main,
    }),
  },
  '&:not([data-focused], [data-selected]):hover': {
    backgroundColor: 'var(--joy-palette-background-level2)',
  },
}));

/**
 * Group (nested items) renderer.
 *
 * This used to be a react-spring animated MUI Collapse, which meant one spring
 * per expandable row driving a JS animation every frame. Expanding a large tree
 * started hundreds of springs at once and the whole UI stalled. MUI already
 * unmounts collapsed groups (`unmountOnExit`), so all this needs to do is
 * render the list when the item is expanded; the reveal is a cheap
 * compositor-only CSS animation defined in CustomTreeItem.scss.
 */
type TreeItemGroupProps = {
  in?: boolean;
  children?: React.ReactNode;
  component?: React.ElementType;
  unmountOnExit?: boolean;
  className?: string;
} & React.HTMLAttributes<HTMLElement>;

const TreeItemGroup = React.memo(function TreeItemGroup({
  in: inProp,
  children,
  component,
  // Consumed by MUI's Collapse API; must not reach the DOM node.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  unmountOnExit,
  className,
  ...other
}: TreeItemGroupProps) {
  if (!inProp) return null;
  const Component = (component ?? 'ul') as React.ElementType;
  return (
    <Component
      {...other}
      className={
        className ? `tree-item-group ${className}` : 'tree-item-group'
      }
    >
      {children}
    </Component>
  );
});

interface ExtendedTreeItemProps {
  fileType?: FileType;
  fileData?: SignatureFacts;
}

interface CustomTreeItemProps
  extends Omit<UseTreeItemParameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {
  onRightClick?: (event: React.MouseEvent, nodeId: string) => void;
  rowIndex: number;
  isRoot: boolean;
  isSelected: boolean;
  showCheckbox?: boolean;
  /** Rendered just before the row's status icons (e.g. a per-row action) */
  preStatusAdornment?: React.ReactNode;
  /** Rendered at the trailing edge of the row (e.g. a per-section action) */
  endAdornment?: React.ReactNode;
  /** When true, the label renders as an inline rename input (folder rename). */
  isRenaming?: boolean;
  onRenameCommit?: (name: string) => void;
  onRenameCancel?: () => void;
  /** Validation or API message for the rename in progress. */
  renameError?: string | null;
  /** True while the rename request is in flight. */
  isRenameSaving?: boolean;
}

export const CustomTreeItem = React.memo(
  React.forwardRef(function CustomTreeItem(
    props: CustomTreeItemProps,
    ref: React.Ref<HTMLLIElement>
  ) {
    const {
      id,
      itemId,
      label,
      disabled,
      children,
      onRightClick,
      rowIndex,
      isRoot,
      isSelected,
      showCheckbox = true,
      preStatusAdornment,
      endAdornment,
      isRenaming,
      onRenameCommit,
      onRenameCancel,
      renameError,
      isRenameSaving,
      ...other
    } = props;

    const {
      getContextProviderProps,
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getGroupTransitionProps,
      getDragAndDropOverlayProps,
      status,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    const item = useTreeItemModel<ExtendedTreeItemProps>(itemId);
    const isFile = item?.fileType !== 'folder';
    const signatureState = getSignatureState(item?.fileData);

    let icon;
    if (status.expandable) {
      icon = FolderRounded;
    } else if (item && item.fileType) {
      icon = getIconFromFileType(item.fileType);
    }
    const isEven = rowIndex % 2 === 0;

    // A plain class list instead of an `sx` prop: `sx` re-serialises styles for
    // every row on every render, which is the single most expensive thing a
    // tree row can do when hundreds of them are mounted at once.
    const surfaceClassName = React.useMemo(() => {
      const variant = isSelected
        ? 'tree-item-surface--selected'
        : isRoot || !isEven
        ? 'tree-item-surface--level1'
        : 'tree-item-surface--body';
      return `tree-item-surface ${variant} tree-item-content-${label}`;
    }, [isSelected, isRoot, isEven, label]);

    return (
      <TreeItemProvider {...getContextProviderProps()}>
        <TreeItemRoot
          isRoot={isRoot}
          isEven={isEven}
          {...getRootProps(other)}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRightClick?.(event, itemId);
          }}
        >
          <div className={surfaceClassName}>
            <TreeItemContent
              {...getContentProps()}
              data-testid={`tree-item-content-${itemId}`}
              data-name={isFile ? 'file' : 'folder'}
              data-node-label={label as string}
              className="file"
            >
              <TreeItemIconContainer
                {...getIconContainerProps()}
                data-testid={`tree-item-icon-${itemId}`}
                data-tree-item-id={itemId}
                data-name={isFile ? 'file' : 'folder'}
                data-node-label={label as string}
              >
                <TreeItemIcon status={status} />
              </TreeItemIconContainer>
              {signatureState && (
                <SignatureChip
                  state={signatureState}
                  signatures={item?.fileData?.signature_info}
                />
              )}
              <div
                className="w-4 h-4 flex items-center"
                data-testid={`tree-item-checkbox-${itemId}`}
              >
                {showCheckbox && <CustomCheckbox {...getCheckboxProps()} />}
              </div>
              <CustomLabel
                {...getLabelProps({
                  icon,
                  expandable: status.expandable && status.expanded,
                })}
                item={item}
                preStatusAdornment={preStatusAdornment}
                endAdornment={endAdornment}
                isRenaming={isRenaming}
                onRenameCommit={onRenameCommit}
                onRenameCancel={onRenameCancel}
                renameError={renameError}
                isRenameSaving={isRenameSaving}
              />
              <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
            </TreeItemContent>
          </div>
          {children && <TreeItemGroup {...getGroupTransitionProps()} />}
        </TreeItemRoot>
      </TreeItemProvider>
    );
  })
);
